import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { sanityClient } from "../lib/sanityClient";
import { fadeUp, stagger, viewport } from "../lib/motion";
import "../styles/ModelFamily.css";

const QUERY = `*[_type == "modelFamilySection"][0]{
  heading, subtext, models, footnote,
  benchmarkText, benchmarkLinkLabel, benchmarkUrl
}`;

// Shown until the benchmark fields are filled in Sanity.
const DEFAULT_BENCHMARK = {
  text: "BharatGPT outperforms a model 15× its size on key reasoning and fairness benchmarks.",
  linkLabel: "View full benchmark details & methodology →",
  url: "https://corover.ai/blogs/bharatgpt-beats-a-model-15x-its-size",
};

// "3B (F16 U8)"    -> { count: "3B", precision: "F16 U8" }
// "5B (E2B, BF16)" -> { count: "5B", precision: "BF16", variant: "E2B" }
const splitParameters = (raw = "") => {
  const match = raw.match(/^(\d+(?:\.\d+)?)([KMB])?\s*(?:\((.*)\))?$/);
  if (!match) return { count: raw, precision: null, variant: null };
  const [, num, unit = "", inner] = match;
  const parts = inner ? inner.split(/,\s*/) : [];
  return {
    count: `${num}${unit}`,
    variant: parts.length > 1 ? parts[0] : null,
    precision: parts.length > 1 ? parts.slice(1).join(", ") : parts[0] || null,
  };
};

const renderHeading = (heading = "") => {
  const [before, after] = heading.split("BharatGPT");
  if (after === undefined) return heading;
  return (
    <>
      {before}Bharat<span className="model-family-highlight">GPT</span>
      {after}
    </>
  );
};

const ModelFamily = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    sanityClient.fetch(QUERY).then(setData);
  }, []);

  if (!data) return null;

  const models = data.models || [];
  const newest = models.length - 1;

  const footnoteBody = (data.footnote || "").replace(/^Why it[’']s different:\s*/, "");
  const reasons = footnoteBody
    .replace(/\.$/, "")
    .split(/\s·\s/)
    .filter(Boolean)
    .map((r) => r.charAt(0).toUpperCase() + r.slice(1));

  return (
    <section className="model-family-section" id="models">
      <div className="model-family-wrapper">
        <motion.div
          className="model-family-head"
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={stagger(0.12)}
        >
          <motion.h2 className="model-family-heading" variants={fadeUp}>
            {renderHeading(data.heading)}
          </motion.h2>
          <motion.p className="model-family-subtext" variants={fadeUp}>
            {data.subtext?.replace(/([-,]\s+)([a-z])/g, (_, sep, c) => sep + c.toUpperCase())}
          </motion.p>
        </motion.div>

        <motion.div
          className="spec"
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={stagger(0.1, 0.1)}
        >
          {models.map((m, i) => {
            const p = splitParameters(m.parameters);
            return (
              <motion.article
                key={m._key}
                className={`spec-col ${i === newest ? "is-newest" : ""}`}
                variants={fadeUp}
              >
                <p className="spec-date">{m.released}</p>
                <h3 className="spec-name">{m.name}</h3>
                <p className="spec-figure">
                  <span className="spec-count">{p.count}</span>
                  <span className="spec-unit">parameters</span>
                </p>
                <div className="spec-cell">
                  <span className="spec-label">Precision</span>
                  <span className="spec-value">
                    {p.precision ? (p.variant ? `${p.precision} · ${p.variant}` : p.precision) : "—"}
                  </span>
                </div>
                <div className="spec-cell">
                  <span className="spec-label">Model size</span>
                  <span className="spec-value">{m.size}</span>
                </div>
                <div className="spec-cell">
                  <span className="spec-label">Base</span>
                  <span className="spec-value">{m.base}</span>
                </div>
              </motion.article>
            );
          })}
        </motion.div>

        {reasons.length > 0 && (
          <motion.div
            className="model-reasons"
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={fadeUp}
          >
            <p className="model-reasons-title">Why it’s different</p>
            <ul className="model-reasons-list">
              {reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
            <p className="model-benchmark">
              {data.benchmarkText || DEFAULT_BENCHMARK.text}{" "}
              <a
                href={data.benchmarkUrl || DEFAULT_BENCHMARK.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {data.benchmarkLinkLabel || DEFAULT_BENCHMARK.linkLabel}
              </a>
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default ModelFamily;
